class PredictiveSearch extends HTMLElement {
  constructor() {
    super();

    this.selectors = {
      searchQuery: "[data-query]",
      searchMessage: "[data-message]",
      input: "[data-search-input]",
      submit: 'button[type="submit"]',
      loading: "[data-spinner]",
      clear: "[data-clear-search]",
      popularSearchItems: ["[data-ps-item]"],
      moreResultIcon: "[data-more-result-icon]"
    };

    this.container = document.querySelector("[data-search-popup]");
    this.domNodes = queryDomNodes(this.selectors, this.container);
    this.cachedResults = {};
    this.transitionDuration = 300;

    this.input = this.querySelector('input[type="search"]');
    this.predictiveSearchResults = this.querySelector(
      "[data-predictive-search]"
    );
    this.searchPopupResults = this.querySelector(
      ".m-search-popup__result"
    );
    this.popularSearches = this.querySelector(
      "[data-popular-searches]"
    );
    this.searchCount = this.querySelector("[data-search-count]");

    this.setupEventListeners();
  }

  setPopularSearchesLink = () => {
    const { popularSearchItems } = this.domNodes;

    popularSearchItems.forEach((item) => {
      item.href = createSearchLink(item.dataset.psQuery);
    });
  };

  setupEventListeners() {
    const { clear } = this.domNodes;
    const form = this.querySelector("form.m-search-form");

    if (form) {
      form.addEventListener(
        "submit",
        this.onFormSubmit.bind(this)
      );
    }

    if (this.input) {
      this.input.addEventListener(
        "input",
        debounce((event) => {
          this.onChange(event);
        }, 300).bind(this)
      );
    }

    if (clear) {
      clear.addEventListener(
        "click",
        this.onClearSearch.bind(this)
      );
    }

    if (
      typeof MinimogEvents !== "undefined" &&
      typeof MinimogTheme !== "undefined"
    ) {
      MinimogEvents.subscribe(
        MinimogTheme.pubSubEvents.closeSearchPopup,
        () => this.close(true)
      );
    }
  }

  getQuery() {
    return this.input ? this.input.value.trim() : "";
  }

  normalizeQuery(query) {
    return query
      .trim()
      .toLowerCase()
      .replace(/\s+/g, " ");
  }

  getCollectionRedirect(query) {
    const collectionRoutes = {
      earring: "/collections/earrings",
      earrings: "/collections/earrings",

      necklace: "/collections/necklaces",
      necklaces: "/collections/necklaces",

      bracelet: "/collections/bracelets",
      bracelets: "/collections/bracelets",

      "phone wristlet": "/collections/phone-wristlets",
      "phone wristlets": "/collections/phone-wristlets",

      "phone charm": "/collections/phone-charm",
      "phone charms": "/collections/phone-charm"
    };

    return collectionRoutes[this.normalizeQuery(query)] || null;
  }

  onChange() {
    const query = this.getQuery();

    if (query.length) {
      this.getSearchResults(query);
    } else {
      this.close(true);
    }
  }

  onFormSubmit(event) {
    const query = this.getQuery();

    if (!query.length) {
      event.preventDefault();
      return;
    }

    const selectedResult = this.querySelector(
      '[aria-selected="true"] a'
    );

    if (selectedResult) {
      return;
    }

    const collectionRedirect = this.getCollectionRedirect(query);

    if (collectionRedirect) {
      event.preventDefault();
      window.location.href = collectionRedirect;
    }
  }

  onClearSearch(event) {
    event.preventDefault();

    if (!this.input) return;

    this.input.value = "";
    this.onChange();
    this.input.focus();
  }

  getSearchResults(query) {
    const cacheKey = this.normalizeQuery(query).replace(/\s+/g, "-");

    this.toggleSpinnerLoading(true);
    this.toggleClearSearch(false);

    const searchByTag =
      this.dataset.searchByTag === "true";

    const searchByBody =
      this.dataset.searchByBody === "true";

    const unavailableProducts =
      this.dataset.unavailableProductsOption || "last";

    let searchableFields =
      "title,product_type,vendor,variants.sku,variants.title";

    /*
     * Correct field mapping:
     * searchByTag adds tag
     * searchByBody adds body
     */
    if (searchByTag) {
      searchableFields += ",tag";
    }

    if (searchByBody) {
      searchableFields += ",body";
    }

    if (this.cachedResults[cacheKey]) {
      this.renderSearchResults(
        this.cachedResults[cacheKey]
      );
      return;
    }

    const predictiveSearchUrl =
      window.MinimogSettings?.routes?.predictive_search_url ||
      "/search/suggest";

    const requestUrl =
      `${predictiveSearchUrl}` +
      `?q=${encodeURIComponent(query)}` +
      `&resources[type]=product` +
      `&resources[limit]=10` +
      `&resources[options][unavailable_products]=${encodeURIComponent(
        unavailableProducts
      )}` +
      `&resources[options][fields]=${encodeURIComponent(
        searchableFields
      )}` +
      `&section_id=predictive-search`;

    fetch(requestUrl)
      .then((response) => {
        if (!response.ok) {
          throw new Error(
            `Predictive search request failed: ${response.status}`
          );
        }

        return response.text();
      })
      .then((html) => {
        const parsedDocument = new DOMParser().parseFromString(
          html,
          "text/html"
        );

        const predictiveSection =
          parsedDocument.querySelector(
            "#shopify-section-predictive-search"
          );

        if (!predictiveSection) {
          throw new Error(
            "Predictive search section was not found."
          );
        }

        const resultsHtml = predictiveSection.innerHTML;

        this.cachedResults[cacheKey] = resultsHtml;
        this.renderSearchResults(resultsHtml);
      })
      .catch((error) => {
        console.error("Predictive search error:", error);

        this.toggleSpinnerLoading(false);
        this.toggleClearSearch(true);
        this.close();
      });
  }

  renderSearchResults(html) {
    if (!this.predictiveSearchResults) return;

    this.predictiveSearchResults.innerHTML = html;
    this.setAttribute("results", true);

    const itemsWrapper = this.querySelector(
      "[data-search-items-wrapper]"
    );

    const hasResults =
      itemsWrapper && itemsWrapper.childElementCount > 0;

    this.renderSearchQueryAndMessage(hasResults);
    this.toggleSpinnerLoading(false);
    this.toggleClearSearch(true);
    this.open();
  }

  renderSearchQueryAndMessage(hasResults) {
    const {
      input,
      searchQuery,
      searchMessage,
      moreResultIcon
    } = this.domNodes;

    if (
      !input ||
      !searchQuery ||
      !searchMessage ||
      !moreResultIcon
    ) {
      return;
    }

    const query = input.value;
    const resultsTitle = searchMessage.dataset.resultsTitle;

    searchQuery.textContent = query;

    if (this.predictiveSearchResults) {
      this.predictiveSearchResults.classList.remove("m:hidden");
    }

    if (this.searchPopupResults) {
      this.searchPopupResults.classList.remove("m:hidden");
    }

    if (hasResults) {
      searchMessage.textContent = resultsTitle;
      moreResultIcon.classList.add("m:hidden");
    } else {
      searchMessage.textContent =
        searchMessage.dataset.resultsTitle;
      moreResultIcon.classList.remove("m:hidden");
    }
  }

  toggleSpinnerLoading(isLoading) {
    const { loading, submit } = this.domNodes;

    if (submit) {
      submit.style.visibility = isLoading
        ? "hidden"
        : "visible";
    }

    if (loading) {
      loading.style.visibility = isLoading
        ? "visible"
        : "hidden";
    }
  }

  toggleClearSearch(showClearButton) {
    const { clear } = this.domNodes;

    if (clear) {
      clear.style.visibility = showClearButton
        ? "visible"
        : "hidden";
    }
  }

  open() {
    const maxHeight =
      this.resultsMaxHeight ||
      this.getResultsMaxHeight();

    if (this.searchPopupResults) {
      this.searchPopupResults.style.setProperty(
        "--search-result-max-height",
        `${maxHeight}px`
      );
    }

    this.setAttribute("open", true);

    if (this.input) {
      this.input.setAttribute("aria-expanded", true);
    }

    this.isOpen = true;

    if (this.popularSearches) {
      this.popularSearches.classList.add("m:hidden");
    }

    if (this.searchCount) {
      this.searchCount.classList.remove("m:hidden");
    }
  }

  close(clearSearch = false) {
    if (clearSearch) {
      if (this.input) {
        this.input.value = "";
      }

      this.removeAttribute("results");
      this.toggleClearSearch(false);
      this.resultsMaxHeight = false;
    }

    this.removeAttribute("open");

    if (this.input) {
      this.input.setAttribute("aria-expanded", false);
    }

    if (this.predictiveSearchResults) {
      this.predictiveSearchResults.classList.add("m:hidden");
    }

    if (this.searchPopupResults) {
      this.searchPopupResults.classList.add("m:hidden");
    }

    if (this.popularSearches) {
      this.popularSearches.classList.remove("m:hidden");
    }

    if (this.searchCount) {
      this.searchCount.classList.add("m:hidden");
    }

    this.isOpen = false;
  }

  getResultsMaxHeight() {
    const predictiveSearchElement =
      document.querySelector("predictive-search");

    if (!predictiveSearchElement) {
      return window.innerHeight;
    }

    this.resultsMaxHeight =
      window.innerHeight -
      predictiveSearchElement.getBoundingClientRect().bottom;

    return this.resultsMaxHeight;
  }
}

if (!customElements.get("predictive-search")) {
  customElements.define(
    "predictive-search",
    PredictiveSearch
  );
}